import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_STEP_RATIO = 0.7;

export interface UseHorizontalScrollResult<T extends HTMLElement> {
    scrollRef: React.RefObject<T | null>;
    canScrollPrev: boolean;
    canScrollNext: boolean;
    hasOverflow: boolean;
    scrollPrev: () => void;
    scrollNext: () => void;
    sync: () => void;
}

export function useHorizontalScroll<
    T extends HTMLElement = HTMLDivElement,
>(): UseHorizontalScrollResult<T> {
    const scrollRef = useRef<T | null>(null);
    const [limits, setLimits] = useState({ prev: false, next: false });

    const sync = useCallback(() => {
        const element = scrollRef.current;
        if (!element) return;

        const maxScroll = element.scrollWidth - element.clientWidth;
        setLimits({
            prev: element.scrollLeft > 1,
            next: element.scrollLeft < maxScroll - 1,
        });
    }, []);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        sync();
        element.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);

        const observer =
            typeof ResizeObserver !== 'undefined' ? new ResizeObserver(sync) : null;
        observer?.observe(element);

        return () => {
            element.removeEventListener('scroll', sync);
            window.removeEventListener('resize', sync);
            observer?.disconnect();
        };
    }, [sync]);

    const scrollByStep = useCallback((direction: 1 | -1) => {
        const element = scrollRef.current;
        if (!element) return;

        const prefersReducedMotion =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        element.scrollBy({
            left: direction * element.clientWidth * SCROLL_STEP_RATIO,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
    }, []);

    return {
        scrollRef,
        canScrollPrev: limits.prev,
        canScrollNext: limits.next,
        hasOverflow: limits.prev || limits.next,
        scrollPrev: useCallback(() => scrollByStep(-1), [scrollByStep]),
        scrollNext: useCallback(() => scrollByStep(1), [scrollByStep]),
        sync,
    };
}
